import { useEffect } from 'react';
import {
  useConnection,
  usePublicClient,
  useReadContract,
  useWriteContract,
  useSimulateContract,
} from 'wagmi';
import SDAOLaunchpadABIUpdate from 'utils/constants/abi/SDAOLockedStaking.abi';
import { useDebounce } from 'use-debounce';
import { Abi, parseAbi } from 'viem';
import {
  addBuffer,
  useEstimateContractGas,
} from 'utils/hooks/useEstimateContractGas';
import { getStakingDepositConfig } from 'structure/staking/utils/stakingDeposit';

export const MAX_LOCK_DAYS = 360;
export const FIRST_MIN_LOCK_DAYS = 30;
export const FIRST_DEFAULT_LOCK_DAYS = 90;

const earlyUnlockFeePerDayAbi = parseAbi([
  'function earlyUnlockFeePerDay() view returns (uint256)',
]);

const logContractError = (
  message: string,
  error: unknown,
  context?: Record<string, unknown>
) => {
  console.error(message, {
    error,
    ...context,
  });
};

type UseDepositParams = {
  contractAddress: `0x${string}`;
  chainId?: number;
  tokenAmount: string;
  lockedPeriod: number;
  enabled: boolean;
};

export const useDeposit = ({
  contractAddress,
  chainId,
  tokenAmount,
  lockedPeriod,
  enabled = true,
}: UseDepositParams) => {
  const [debouncedTokenAmount] = useDebounce(tokenAmount, 750);
  const [debouncedLockedPeriod] = useDebounce(lockedPeriod, 750);

  const account = useConnection();

  const config = getStakingDepositConfig({
    account: account.address,
    chainId,
    contractAddress,
    enabled,
    lockedPeriod: debouncedLockedPeriod,
    tokenAmount: debouncedTokenAmount,
  });

  const prepare = useSimulateContract(config);
  const publicClient = usePublicClient({ chainId });

  const prepareFresh = async () => {
    if (!publicClient || !account.address) {
      throw new Error('Staking deposit client is not available');
    }

    const { query: _query, ...request } = getStakingDepositConfig({
      account: account.address,
      chainId,
      contractAddress,
      enabled: true,
      lockedPeriod,
      tokenAmount,
    });
    const estimatedGas = await publicClient.estimateContractGas(request);

    return publicClient.simulateContract({
      ...request,
      gas: addBuffer(estimatedGas, chainId),
    });
  };

  useEffect(() => {
    if (prepare.error) {
      logContractError('Error preparing deposit transaction', prepare.error, {
        contractAddress,
        tokenAmount: debouncedTokenAmount,
        lockedPeriod: debouncedLockedPeriod,
      });
    }
  }, [prepare.error]);

  const contract = useWriteContract();

  return { prepare, prepareFresh, contract };
};

type UseWithdrawParams = {
  contractAddress: `0x${string}`;
  tokenAmount: string;
  enabled: boolean;
};

export const useWithdraw = ({
  contractAddress,
  tokenAmount,
  enabled = true,
}: UseWithdrawParams) => {
  const account = useConnection();
  const args = {
    address: contractAddress,
    account: account.address,
    abi: SDAOLaunchpadABIUpdate as Abi,
    functionName: 'withdrawAndClaim',
    args: [tokenAmount],
    enabled: enabled,
  };

  const gas = useEstimateContractGas(args, [tokenAmount, enabled]);
  const prepare = useSimulateContract({
    ...args,
    gas,
  });

  useEffect(() => {
    if (prepare.error) {
      logContractError('Error preparing withdraw transaction', prepare.error, {
        contractAddress,
        tokenAmount,
      });
    }
  }, [prepare.error]);

  const contract = useWriteContract();

  return { prepare, contract };
};

type UseStakingInfoParams = {
  contractAddress: `0x${string}`;
  address: `0x${string}`;
  enabled: boolean;
};

export const useAddressStakingInfo = ({
  contractAddress,
  address,
  enabled = true,
}: UseStakingInfoParams) => {
  return useReadContract({
    address: contractAddress,
    abi: SDAOLaunchpadABIUpdate,
    functionName: 'userInfo',
    args: [address],
    query: {
      enabled,
    },
  });
};

type UseEarlyUnlockFeePerDayParams = {
  contractAddress: `0x${string}`;
  chainId: number;
  enabled: boolean;
};

export const useEarlyUnlockFeePerDay = ({
  contractAddress,
  chainId,
  enabled,
}: UseEarlyUnlockFeePerDayParams) =>
  useReadContract({
    address: contractAddress,
    abi: earlyUnlockFeePerDayAbi,
    functionName: 'earlyUnlockFeePerDay',
    chainId,
    query: { enabled },
  });

type UseClaimPendingInfoParams = {
  contractAddress: `0x${string}`;
  address: `0x${string}`;
  enabled: boolean;
};

export const useClaimPendingInfo = ({
  contractAddress,
  address,
  enabled = true,
}: UseClaimPendingInfoParams) => {
  const [debouncedAddress] = useDebounce(address, 500);
  return useReadContract({
    address: contractAddress,
    abi: SDAOLaunchpadABIUpdate,
    functionName: 'pendingFor',
    args: [debouncedAddress],
    query: {
      enabled,
    },
  });
};

type UseClaimParams = {
  contractAddress: `0x${string}`;
  enabled: boolean;
};

export const useClaim = ({
  contractAddress,
  enabled = true,
}: UseClaimParams) => {
  const account = useConnection();

  const args = {
    address: contractAddress,
    account: account.address,
    abi: SDAOLaunchpadABIUpdate as Abi,
    functionName: 'claim',
    args: [],
    enabled,
  };

  const gas = useEstimateContractGas(args, [enabled]);
  const prepare = useSimulateContract({
    ...args,
    gas,
  });

  useEffect(() => {
    if (prepare.error) {
      logContractError('Error preparing claim transaction', prepare.error, {
        contractAddress,
      });
    }
  }, [prepare.error]);

  const contract = useWriteContract();

  return { prepare, contract };
};
